import { Box, Grid, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { IServiceOrderLogReportFilters } from "./Filters";
import { useLanguageContext } from "../../../../../contexts/LanguageContext";
import { IDefaultStatusData, ServiceOrderLogTypeData, ServiceOrderLogTypeEnum, ServicePriorityData, ServicePropertyData, ServicePropertyEnum, ServiceStatusData, ServiceStatusEnum } from "../../../../../data/Enums";
import { useCallback, useEffect, useMemo } from "react";
import IServiceOrderLog from '../../../../../interfaces/IServiceOrderLog';
import FormHelper from '../../../../../helpers/FormHelper';
import DateTimeHelper from '../../../../../helpers/DateTimeHelper';
import IUser from "../../../../../interfaces/IUser";
import DefaultConstants from "../../../../../data/Constants";
import { Description, HomeRepairService, People } from "@mui/icons-material";
import ArrayHelper from "../../../../../helpers/ArrayHelper";
import NumberHelper from "../../../../../helpers/NumberHelper";
import useState from "../../../../../hooks/useState";
import ServiceOrderLogService from "../../../../../services/ServiceOrderLogService";
import IServiceOrder from "../../../../../interfaces/IServiceOrder";



const attachmentPropertyData = ServicePropertyData[ServicePropertyEnum.Attachments];


interface IStateValues {
    serviceOrderLog: IServiceOrderLog[];
}

const ServiceOrderLogContent: React.FunctionComponent<IServiceOrderLogReportFilters> = (filters) => {
    const { translate } = useLanguageContext();

    const state = useState<IStateValues>({
        serviceOrderLog: []
    });


    useEffect(() => {
        let formData = FormHelper.Create(undefined);
        
        Object.entries(filters).forEach(([key, value]) => {
            if (key === 'enterprise') {
                key = 'enterpriseId';
                value = value.id;
            } else if (key === 'user') {
                value = value.map((v: IUser) => v.id).join(',');
            } else if (key === 'action') {
                key = 'type';
                value = value.join(',');
            } else if (key === 'initialStatus') {
                value = value.map((v: IDefaultStatusData) => v.key).join(',');
            } else if (key === 'finalStatus') {
                value = value.map((v: IDefaultStatusData) => v.key).join(',');
            }

            formData = FormHelper.Add(formData, key, value);
        });

        ServiceOrderLogService.List(formData).then(resp => {
            state.update('serviceOrderLog', resp.data.serviceOrderLog as IServiceOrderLog[]);
        }).catch(err => {
            state.update('serviceOrderLog', []);
        });

        // eslint-disable-next-line
    }, []);


    const renderFilterFieldset = useMemo(() => {
        return <TableCell className='reportpreview-paper-content-fieldset'>
            <Typography variant='caption' className='reportpreview-paper-content-fieldset-label'>
                {translate('filters')}
            </Typography>

            <Grid container>
                <Grid xs={3} className='reportpreview-paper-content-fieldset-item'>
                    <Typography variant='subtitle1' className='reportpreview-paper-content-fieldset-item-title'>
                        {translate('startDate')}
                    </Typography>

                    <Typography variant='body1' className='reportpreview-paper-content-fieldset-item-value'>
                        {DateTimeHelper.Format(filters.startDate, 'DD/MM/YYYY')}
                    </Typography>
                </Grid>

                <Grid xs={3} className='reportpreview-paper-content-fieldset-item'>
                    <Typography variant='subtitle1' className='reportpreview-paper-content-fieldset-item-title'>
                        {translate('endDate')}
                    </Typography>

                    <Typography variant='body1' className='reportpreview-paper-content-fieldset-item-value'>
                        {DateTimeHelper.Format(filters.endDate, 'DD/MM/YYYY')}
                    </Typography>
                </Grid>

                <Grid xs={filters.action.length === 0 ? 3 : 6} className='reportpreview-paper-content-fieldset-item'>
                    <Typography variant='subtitle1' className='reportpreview-paper-content-fieldset-item-title'>
                        {translate('action')}
                    </Typography>

                    <Typography variant='body1' className='reportpreview-paper-content-fieldset-item-value'>
                        {
                            filters.action.length === 0 ? (
                                translate('all')
                            ) : (
                                filters.action.map(t => translate(ServiceOrderLogTypeData[t].name)).join(', ')
                            )
                        }
                    </Typography>
                </Grid>

                <Grid xs={3} className='reportpreview-paper-content-fieldset-item'>
                    <Typography variant='subtitle1' className='reportpreview-paper-content-fieldset-item-title'>
                        {translate('serviceOrder')}
                    </Typography>

                    <Typography variant='body1' className='reportpreview-paper-content-fieldset-item-value'>
                        {
                            filters.serviceOrder.length === 0 ? (
                                translate('all')
                            ) : (
                                filters.serviceOrder.split(',').map(s => s.trim()).join(', ')
                            )
                        }
                    </Typography>
                </Grid>

                <Grid xs={3} className='reportpreview-paper-content-fieldset-item'>
                    <Typography variant='subtitle1' className='reportpreview-paper-content-fieldset-item-title'>
                        {translate('initialStatus')}
                    </Typography>

                    <Typography variant='body1' className='reportpreview-paper-content-fieldset-item-value'>
                        {
                            filters.initialStatus.length === 0 ? (
                                translate('all')
                            ) : (
                                filters.initialStatus.map(s => translate(s.name)).join(', ')
                            )
                        }
                    </Typography>
                </Grid>

                <Grid xs={3} className='reportpreview-paper-content-fieldset-item'>
                    <Typography variant='subtitle1' className='reportpreview-paper-content-fieldset-item-title'>
                        {translate('finalStatus')}
                    </Typography>

                    <Typography variant='body1' className='reportpreview-paper-content-fieldset-item-value'>
                        {
                            filters.finalStatus.length === 0 ? (
                                translate('all')
                            ) : (
                                filters.finalStatus.map(s => translate(s.name)).join(', ')
                            )
                        }
                    </Typography>
                </Grid>

                <Grid xs={3} className='reportpreview-paper-content-fieldset-item'>
                    <Typography variant='subtitle1' className='reportpreview-paper-content-fieldset-item-title'>
                        {translate('user')}
                    </Typography>

                    <Typography variant='body1' className='reportpreview-paper-content-fieldset-item-value'>
                        {
                            filters.user.length === 0 ? (
                                translate('all')
                            ) : (
                                filters.user.map(u => u.login).join(', ')
                            )
                        }
                    </Typography>
                </Grid>
            </Grid>
        </TableCell>
    }, [filters, translate]);


    const data = useMemo(() => {
        return state.values.serviceOrderLog;
        // eslint-disable-next-line
    }, [state.values.serviceOrderLog, filters]);


    const createStatusText = useCallback((statusId: number) => {
        const status = Object.values(ServiceStatusData).find(s => s.key === statusId) as IDefaultStatusData;
        
        return <Typography className='text-title' sx={{ color: `${status.color} !important` }}>
            <status.Icon />
            {translate(status.name)}
        </Typography>;
        // eslint-disable-next-line
    }, []);


    const createPriorityText = useCallback((priorityId: number) => {
        const priority = Object.values(ServicePriorityData).find(s => s.key === priorityId) as IDefaultStatusData;
        
        return <Typography className='text-title' sx={{ color: `${priority.color} !important` }}>
            <priority.Icon />
            {translate(priority.name)}
        </Typography>;
        // eslint-disable-next-line
    }, []);


    const createDateText = useCallback((date: string | null | undefined) => {
        if (date === undefined || date === null || date.length === 0) {
            return '-';
        }

        return DateTimeHelper.Format(date);
        // eslint-disable-next-line
    }, []);


    const _getStatusData = useCallback((statusEnum: ServiceStatusEnum) => {
        const statusData = ServiceStatusData[statusEnum];

        return {
            ...statusData,
            execution: data.filter(row => +row.previousValue === statusData.key)
                .reduce((acc, curr) => {
                    acc.time += +(curr.executionTime ?? 0);
                    acc.length += 1;
                    return acc;
                }, { time: 0, length: 0})
        };
    }, [data]);

    
    return <>
        <TableRow>
            {renderFilterFieldset}
        </TableRow>

        <TableRow>
            <TableCell className='border-none' padding='none'>
                {
                    data.length > 0 ? (
                        <>
                            <Table className='data-table' sx={{ marginY: DefaultConstants.gridRowSpacing }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell width={80}>{translate('serviceOrder')}</TableCell>
                                        <TableCell width={200}>{translate('action')}</TableCell>
                                        <TableCell>{translate('changedFrom')}</TableCell>
                                        <TableCell>{translate('to')}</TableCell>
                                        <TableCell>{translate('user')}</TableCell>
                                        <TableCell>{translate('datetime')}</TableCell>
                                        <TableCell />
                                    </TableRow>
                                </TableHead>
                                
                                <TableBody className='tbody-striped'>
                                    {
                                        data.map(row => {
                                            let typeText = undefined;
                                            let previousValueText = (row.previousValue ?? '-') as any;
                                            let newValueText = (row.newValue ?? '-') as any;
                                            
                                            if (row.type === ServiceOrderLogTypeEnum.UpdateStatus) {
                                                typeText = '_serviceOrderLogChangeStatusAction';

                                                previousValueText = createStatusText(+row.previousValue);
                                                newValueText = createStatusText(+row.newValue);
                                            } 
                                            
                                            else if (row.type === ServiceOrderLogTypeEnum.UpdatePriority) {
                                                typeText = '_serviceOrderLogChangePriorityAction';
                                                
                                                previousValueText = createPriorityText(+row.previousValue);
                                                newValueText = createPriorityText(+row.newValue);
                                            } 
                                            
                                            else if (row.type === ServiceOrderLogTypeEnum.UpdateDepartment) {
                                                typeText = '_serviceOrderLogChangeDepartmentAction';
                                            } 
                                            
                                            else if (row.type === ServiceOrderLogTypeEnum.UpdateResponsible) {
                                                typeText = '_serviceOrderLogChangeResponsibleAction';
                                            }
                                            
                                            else if (row.type === ServiceOrderLogTypeEnum.UpdateStartDate) {
                                                typeText = '_serviceOrderLogChangeStartDateAction';
                                                
                                                previousValueText = createDateText(row.previousValue);
                                                newValueText = createDateText(row.newValue);
                                            } 
                                            
                                            else {
                                                typeText = '_serviceOrderLogChangeEstimatedCompletionDateAction';
                                                
                                                previousValueText = createDateText(row.previousValue);
                                                newValueText = createDateText(row.newValue);
                                            } 

                                            return <TableRow key={row.id}>
                                                <TableCell>
                                                    <Typography className='text-title'> # {row.serviceOrder?.id ?? '-'} </Typography>
                                                </TableCell>

                                                <TableCell>
                                                    <Typography className='text-title fontweight-normal'> {translate(typeText)} </Typography>
                                                    {
                                                        row.executionTime && <Typography className='text-subtitle'> 
                                                            {translate('executedAt')}
                                                            {' '}
                                                            {DateTimeHelper.ToElapsedTime(row.executionTime)}
                                                        </Typography>
                                                    }
                                                </TableCell>

                                                <TableCell>
                                                    {previousValueText}
                                                </TableCell>

                                                <TableCell>
                                                    {newValueText}
                                                </TableCell>

                                                <TableCell>
                                                    <Typography className='text-title'> {row.user.login} </Typography>
                                                    <Typography className='text-subtitle'> {row.user.fullName} </Typography>
                                                </TableCell>

                                                <TableCell>
                                                    <Typography className='text-title'> {DateTimeHelper.Format(row.updatedAt, 'DD/MM/YYYY')} </Typography>
                                                    <Typography className='text-subtitle'> {DateTimeHelper.Format(row.updatedAt, 'HH:mm:ss')} </Typography>
                                                </TableCell>

                                                <TableCell className='display-inlineflex'>
                                                    {
                                                        row.attachments !== undefined && row.attachments.length > 0 && (
                                                            <Box className='verticalstatus-box width-auto'>
                                                                <attachmentPropertyData.Icon />
                                                                <Typography>{row.attachments.length} {translate(attachmentPropertyData.name).toLowerCase()}</Typography>
                                                            </Box>
                                                        )
                                                    }
                                                </TableCell>
                                            </TableRow>
                                        })
                                    }
                                </TableBody>
                            </Table>

                            {/* Summary */}
                            <Table className='page-break show-only-in-print'>
                                {renderFilterFieldset}
                            </Table>

                            <Table className='data-table' sx={{ marginTop: DefaultConstants.gridRowSpacing }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell colSpan={2}>{translate('summary')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                
                                <TableBody className='tbody-striped'>
                                    <TableRow>
                                        <TableCell className='color-primary display-flex alignitems-center fontweight-600 gap-10'>
                                            <Description className='fontsize-20' />
                                            {translate('recordsFound')}
                                        </TableCell>
                                        <TableCell className='textalign-right fontweight-600'>{data.length}</TableCell>
                                    </TableRow>

                                    <TableRow>
                                        <TableCell className='display-flex alignitems-center fontweight-600 gap-10'>
                                            <HomeRepairService className='fontsize-20' />
                                            {translate('differentServiceOrders')}
                                        </TableCell>
                                        <TableCell className='textalign-right fontweight-600'>
                                            {ArrayHelper.DistinctBy(data.filter(row => row.serviceOrder).map(row => row.serviceOrder) as IServiceOrder[], 'id').length}
                                        </TableCell>
                                    </TableRow>

                                    <TableRow>
                                        <TableCell className='display-flex alignitems-center fontweight-600 gap-10'>
                                            <People className='fontsize-20' />
                                            {translate('differentUsers')}
                                        </TableCell>
                                        <TableCell className='textalign-right fontweight-600'>
                                            {ArrayHelper.DistinctBy(data.map(row => row.user), 'id').length}
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>

                            {/* Statistics */}
                            <Table className='data-table' sx={{ marginTop: DefaultConstants.gridRowSpacing }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell colSpan={2}>{translate('statusStatistics')}</TableCell>
                                        <TableCell className='textalign-right'>{translate('totalTime')}</TableCell>
                                        <TableCell className='textalign-right'>{translate('averageTime')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                
                                <TableBody className='tbody-striped'>
                                    {
                                        [
                                            _getStatusData(ServiceStatusEnum.Pending), 
                                            _getStatusData(ServiceStatusEnum.Open),
                                            _getStatusData(ServiceStatusEnum.InProgress),
                                            _getStatusData(ServiceStatusEnum.Inspection),
                                            _getStatusData(ServiceStatusEnum.Completed)
                                        ].map((status, key) => {
                                            const { time: executionTime, length: executionLength } = status.execution;

                                            const average = NumberHelper.Average(executionTime, executionLength)

                                            return <>
                                                <TableRow>
                                                    <TableCell 
                                                        className='display-flex alignitems-center fontweight-600 gap-10' 
                                                        sx={{
                                                            color: `${status.color} !important`
                                                        }}
                                                    >
                                                        <status.Icon className='fontsize-20' />
                                                        {translate(status.name)}
                                                    </TableCell>

                                                    <TableCell className='fontweight-600'>
                                                        {executionLength} {translate('records')}
                                                    </TableCell>

                                                    <TableCell className='textalign-right fontweight-600'>
                                                        {DateTimeHelper.ToElapsedTime(executionTime)}
                                                    </TableCell>

                                                    <TableCell className='textalign-right fontweight-600'>
                                                        {DateTimeHelper.ToElapsedTime(Math.ceil(average))}
                                                    </TableCell>
                                                </TableRow>
                                            </>;
                                        })
                                    }
                                </TableBody>
                            </Table>
                        </>
                    ) : (
                        <Box className='reportpreview-paper-content-notfound'>
                            <Typography variant='body1'>{translate('_roomLogEmptyRoomList')}</Typography>
                        </Box>
                    )
                }
            </TableCell>
        </TableRow>
    </>;
}

export default ServiceOrderLogContent;